import type { ReactNode } from "react";
import { toExcerptText } from "@/lib/excerpt-text";

/**
 * Pure helpers for the Home blocks — payload projection, title
 * extraction, relative-time formatting, and the inline-markdown
 * renderer. Kept out of the block components so the components stay
 * presentation-only and these stay unit-testable.
 */

/** Shape a raw `visitorNewsPost` payload into the fields the
 *  featured-article hero needs. The BE returns the post as `JSON`
 *  (Apollo unknown); we defensively extract strings + the populated
 *  Media `url`. */
export function extractFeaturedPost(raw: unknown): {
  id: string;
  title: string;
  excerpt: string;
  coverImageUrl?: string;
} | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = String(o._id ?? o.id ?? "");
  if (!id) return null;
  // Cover image — prefer the operator-uploaded Media (`coverImageUrl.url`)
  // and fall back to the externally-pulled URL field that the ingest
  // pipeline mirrors onto the post for RSS/webhook posts.
  const mediaCover = (o.coverImageUrl as Record<string, unknown> | undefined)?.url;
  const externalCover = o.externalCoverImageUrl;
  const cover =
    typeof mediaCover === "string"
      ? mediaCover
      : typeof externalCover === "string"
        ? externalCover
        : undefined;
  return {
    id,
    title: typeof o.title === "string" ? o.title : "",
    excerpt: typeof o.excerpt === "string" ? toExcerptText(o.excerpt, 160) : "",
    coverImageUrl: cover,
  };
}

/** A home news-preview row: the post id (to open the article directly) + its
 *  title. Rows with no title are dropped. */
export interface NewsPreviewItem {
  id: string;
  title: string;
}

/** Read `{ id, title }` rows from the operator's dashboard-prefetched news
 *  array. */
export function extractNewsItemsFromRaw(raw: unknown[] | undefined): NewsPreviewItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): NewsPreviewItem | null => {
      if (typeof item !== "object" || item === null) return null;
      const o = item as Record<string, unknown>;
      const title = typeof o.title === "string" ? o.title : "";
      if (!title) return null;
      return { id: String(o._id ?? o.id ?? ""), title };
    })
    .filter((x): x is NewsPreviewItem => x !== null);
}

/** Read `{ id, title }` rows from the typed `visitorNewsList.items` (slim)
 *  payload. */
export function extractNewsItemsFromList(
  items: ReadonlyArray<{ _id?: string; id?: string; title: string }> | null | undefined,
  error: unknown,
): NewsPreviewItem[] {
  if (error || !items) return [];
  return items
    .map((item): NewsPreviewItem | null => {
      const title = typeof item.title === "string" ? item.title : "";
      if (!title) return null;
      return { id: String(item._id ?? item.id ?? ""), title };
    })
    .filter((x): x is NewsPreviewItem => x !== null);
}

/** Compact relative time for the recent-message timestamp — "4h" /
 *  "2d" / "Just now". Avoids pulling in a full date-fns dependency.
 *  NOTE: "Just now" is English-only; localizing it is a follow-up. */
export function formatRelativeTime(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const diffMs = Date.now() - ts;
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(ts).toLocaleDateString();
}

/**
 * Lightweight inline-markdown renderer for the rich-text block — bold /
 * italic / links / line breaks only (the BE doc allowlist), so the Home
 * tab doesn't pull in the full markdown stack.
 */
export function renderInlineMarkdown(md: string): ReactNode {
  return md.split(/\n{2,}/).map((para, pi) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: markdown paragraphs are split deterministically and order-stable
    <p key={pi} className={pi > 0 ? "mt-2" : undefined}>
      {para.split("\n").map((line, li) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: lines are split deterministically and order-stable
        <span key={li}>
          {li > 0 && <br />}
          {inlineTokens(line)}
        </span>
      ))}
    </p>
  ));
}

function inlineTokens(line: string): ReactNode[] {
  // `[text](url)`, `**bold**`, `*italic*` — order matters.
  const out: ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex walk
  while ((m = re.exec(line))) {
    if (m.index > last) out.push(line.slice(last, m.index));
    if (m[1] && m[2]) {
      const href = /^https?:\/\//.test(m[2]) ? m[2] : "#";
      out.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
          {m[1]}
        </a>,
      );
    } else if (m[3]) {
      out.push(
        <strong key={key++} className="font-semibold text-wx-fg">
          {m[3]}
        </strong>,
      );
    } else if (m[4]) {
      out.push(<em key={key++}>{m[4]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}
